import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/auth/AuthLayout";
import RoleSelectionGrid from "@/components/auth/RoleSelectionGrid";
import {
  Field, Input, Select, Checkbox, SubmitButton, ErrorBanner
} from "@/components/auth/FormPrimitives";
import LogoUpload from "@/components/auth/LogoUpload";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/utils/api";
import { consumePostAuthRedirect } from "@/utils/postAuthRedirect";

/**
 * Shown to users who signed up via Google and don't have a role yet.
 * Mirrors the signup form structure but hits /api/auth/complete-profile/* endpoints.
 */
export default function CompleteProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);

  if (user?.role) {
    // Already completed
    navigate(consumePostAuthRedirect(), { replace: true });
    return null;
  }

  return (
    <AuthLayout
      title="Finish setting up your account"
      subtitle="We need a few more details before you can start using tots360."
      sideTitle={`Hi ${user?.full_name?.split(" ")[0] || "there"} — welcome to tots360.`}
      sideBullets={[
        "Your Google email is already verified ✓",
        "Choose your role — this determines what you can access",
        "You can't change your role later without contacting support",
      ]}
    >
      {!role ? (
        <>
          <p className="text-sm text-slate-400 mb-4">Choose your role:</p>
          <RoleSelectionGrid value={role} onChange={setRole} />
        </>
      ) : (
        <div>
          <button
            type="button"
            onClick={() => setRole(null)}
            data-testid="complete-change-role-button"
            className="text-xs text-slate-400 hover:text-slate-200 mb-4 inline-flex items-center gap-1"
          >
            ← Change role
          </button>
          {role === "parent" && <ParentComplete refresh={refreshUser} navigate={navigate} />}
          {role === "doctor" && <DoctorComplete refresh={refreshUser} navigate={navigate} />}
          {role === "school" && <SchoolComplete refresh={refreshUser} navigate={navigate} />}
          {role === "class_teacher" && <TeacherComplete refresh={refreshUser} navigate={navigate} />}
        </div>
      )}
    </AuthLayout>
  );
}

function SharedContactFields({ values, set, prefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Phone" required htmlFor={`${prefix}-phone`}>
        <Input
          id={`${prefix}-phone`}
          testid={`${prefix}-phone`}
          type="tel"
          required
          value={values.phone}
          onChange={(e) => set("phone", e.target.value)}
          placeholder="+91 98765 43210"
        />
      </Field>
      <Field label="Country" required htmlFor={`${prefix}-country`}>
        <Input
          id={`${prefix}-country`}
          testid={`${prefix}-country`}
          required
          value={values.country}
          onChange={(e) => set("country", e.target.value)}
          placeholder="India"
        />
      </Field>
      <Field label="Timezone" required htmlFor={`${prefix}-tz`}>
        <Input
          id={`${prefix}-tz`}
          testid={`${prefix}-tz`}
          required
          value={values.timezone}
          onChange={(e) => set("timezone", e.target.value)}
          placeholder="Asia/Kolkata"
        />
      </Field>
    </div>
  );
}

function ParentComplete({ refresh, navigate }) {
  const [values, setValues] = useState({
    phone: "", country: "India", timezone: "Asia/Kolkata", relationship: "Father",
    accept_terms: false, accept_privacy: false, accept_medical_disclaimer: false,
  });
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/api/auth/complete-profile/parent", values);
      await refresh();
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="complete-parent-form">
      <SharedContactFields values={values} set={set} prefix="complete-parent" />
      <Field label="Relationship to child(ren)" required htmlFor="complete-parent-rel">
        <Input id="complete-parent-rel" testid="complete-parent-rel" required
          value={values.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="Father / Mother / Guardian" />
      </Field>
      <div className="pt-2 space-y-2">
        <Checkbox testid="complete-parent-terms" checked={values.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)}>
          I agree to the Terms of Service
        </Checkbox>
        <Checkbox testid="complete-parent-privacy" checked={values.accept_privacy} onChange={(e) => set("accept_privacy", e.target.checked)}>
          I have read the Privacy Policy
        </Checkbox>
        <Checkbox testid="complete-parent-disclaimer" checked={values.accept_medical_disclaimer} onChange={(e) => set("accept_medical_disclaimer", e.target.checked)}>
          I acknowledge the Medical Disclaimer
        </Checkbox>
      </div>
      <ErrorBanner error={error} />
      <SubmitButton loading={loading} testid="complete-parent-submit">Complete setup</SubmitButton>
    </form>
  );
}

function DoctorComplete({ refresh, navigate }) {
  const [values, setValues] = useState({
    phone: "", country: "India", timezone: "Asia/Kolkata",
    specialty: "", license_number: "",
    accept_terms: false, accept_privacy: false,
  });
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/api/auth/complete-profile/doctor", values);
      await refresh();
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="complete-doctor-form">
      <SharedContactFields values={values} set={set} prefix="complete-doctor" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Specialty" htmlFor="complete-doctor-spec">
          <Input id="complete-doctor-spec" testid="complete-doctor-spec"
            value={values.specialty} onChange={(e) => set("specialty", e.target.value)} placeholder="Pediatrics" />
        </Field>
        <Field label="License number" htmlFor="complete-doctor-license">
          <Input id="complete-doctor-license" testid="complete-doctor-license"
            value={values.license_number} onChange={(e) => set("license_number", e.target.value)} placeholder="Registration ID" />
        </Field>
      </div>
      <div className="pt-2 space-y-2">
        <Checkbox testid="complete-doctor-terms" checked={values.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)}>
          I agree to the Terms of Service
        </Checkbox>
        <Checkbox testid="complete-doctor-privacy" checked={values.accept_privacy} onChange={(e) => set("accept_privacy", e.target.checked)}>
          I have read the Privacy Policy
        </Checkbox>
      </div>
      <ErrorBanner error={error} />
      <SubmitButton loading={loading} testid="complete-doctor-submit">Complete setup</SubmitButton>
    </form>
  );
}

function SchoolComplete({ refresh, navigate }) {
  const [values, setValues] = useState({
    phone: "", country: "India", timezone: "Asia/Kolkata",
    school_name: "", school_address: "", school_board: "CBSE", logo_path: null,
    accept_terms: false, accept_privacy: false,
  });
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (!values.logo_path) { setError("Please upload your school logo."); return; }
    setLoading(true);
    try {
      await api.post("/api/auth/complete-profile/school", values);
      await refresh();
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="complete-school-form">
      <SharedContactFields values={values} set={set} prefix="complete-school" />
      <Field label="School name" required htmlFor="complete-school-name">
        <Input id="complete-school-name" testid="complete-school-name" required
          value={values.school_name} onChange={(e) => set("school_name", e.target.value)} placeholder="Greenwood High" />
      </Field>
      <Field label="School address" required htmlFor="complete-school-addr">
        <Input id="complete-school-addr" testid="complete-school-addr" required
          value={values.school_address} onChange={(e) => set("school_address", e.target.value)} placeholder="Street, City, State" />
      </Field>
      <Field label="Board" required htmlFor="complete-school-board">
        <Select id="complete-school-board" testid="complete-school-board"
          value={values.school_board} onChange={(e) => set("school_board", e.target.value)}>
          <option value="CBSE">CBSE</option>
          <option value="ICSE">ICSE</option>
          <option value="State">State Board</option>
          <option value="IB">IB</option>
          <option value="Other">Other</option>
        </Select>
      </Field>
      <LogoUpload onUploaded={(p) => set("logo_path", p)} testidPrefix="complete-school-logo" />
      <div className="pt-2 space-y-2">
        <Checkbox testid="complete-school-terms" checked={values.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)}>
          I agree to the Terms of Service
        </Checkbox>
        <Checkbox testid="complete-school-privacy" checked={values.accept_privacy} onChange={(e) => set("accept_privacy", e.target.checked)}>
          I have read the Privacy Policy
        </Checkbox>
      </div>
      <ErrorBanner error={error} />
      <SubmitButton loading={loading} testid="complete-school-submit">Complete setup</SubmitButton>
    </form>
  );
}

function TeacherComplete({ refresh, navigate }) {
  const [values, setValues] = useState({
    phone: "", country: "India", timezone: "Asia/Kolkata",
    invite_code: "", class_grade: "",
    accept_terms: false, accept_privacy: false,
  });
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteState, setInviteState] = useState({ valid: null, schoolName: "" });

  const validate = async (code) => {
    const c = code.trim().toUpperCase();
    if (c.length !== 8) { setInviteState({ valid: null, schoolName: "" }); return; }
    try {
      const { data } = await api.get(`/api/auth/teacher-invite/${c}`);
      setInviteState({ valid: true, schoolName: data.school_name || "" });
    } catch {
      setInviteState({ valid: false, schoolName: "" });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (inviteState.valid !== true) { setError("Enter a valid invite code."); return; }
    setLoading(true);
    try {
      await api.post("/api/auth/complete-profile/class-teacher", { ...values, invite_code: values.invite_code.toUpperCase() });
      await refresh();
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid="complete-teacher-form">
      <Field label="School invite code" required htmlFor="complete-teacher-code" hint="Ask your School admin for an 8-character code.">
        <Input id="complete-teacher-code" testid="complete-teacher-code" required maxLength={8}
          value={values.invite_code}
          onChange={(e) => { const up = e.target.value.toUpperCase(); set("invite_code", up); validate(up); }}
          placeholder="R4K9XP2M" className="uppercase tracking-widest" />
        {inviteState.valid === true && (
          <p className="text-xs text-emerald-300 mt-1">✓ Valid for <strong>{inviteState.schoolName}</strong></p>
        )}
        {inviteState.valid === false && (
          <p className="text-xs text-rose-300 mt-1">Invalid or already used code.</p>
        )}
      </Field>
      <SharedContactFields values={values} set={set} prefix="complete-teacher" />
      <Field label="Class / Grade" required htmlFor="complete-teacher-grade">
        <Input id="complete-teacher-grade" testid="complete-teacher-grade" required
          value={values.class_grade} onChange={(e) => set("class_grade", e.target.value)} placeholder="Grade 5 - B" />
      </Field>
      <div className="pt-2 space-y-2">
        <Checkbox testid="complete-teacher-terms" checked={values.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)}>
          I agree to the Terms of Service
        </Checkbox>
        <Checkbox testid="complete-teacher-privacy" checked={values.accept_privacy} onChange={(e) => set("accept_privacy", e.target.checked)}>
          I have read the Privacy Policy
        </Checkbox>
      </div>
      <ErrorBanner error={error} />
      <SubmitButton loading={loading} testid="complete-teacher-submit">Complete setup</SubmitButton>
    </form>
  );
}
